// src/pages/sales/InvoicesPage.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { usePermissions } from '../../utils/permissions';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  InputAdornment,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tooltip,
  Divider,
  FormControl,
  Select,
  InputLabel,
  Alert,
  Link as MuiLink,
  useTheme,
  alpha,
  Fade,
  Stack,
  LinearProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  SortByAlpha as SortIcon,
  AttachMoney as MoneyIcon,
  Receipt as ReceiptIcon,
  Assignment as AssignmentIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  AccessTime as AccessTimeIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  MonetizationOn as MonetizationOnIcon,
  Home as HomeIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

// Styled components
const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
  '&:hover': {
    backgroundColor: theme.palette.action.selected,
  },
  cursor: 'pointer'
}));

const StatusChip = styled(Chip)(({ theme, status }) => {
  let color = 'default';
  switch (status) {
    case 'paid':
      color = 'success';
      break;
    case 'pending':
      color = 'warning';
      break;
    case 'overdue':
      color = 'error';
      break;
    case 'draft':
      color = 'default';
      break;
    case 'sent':
      color = 'info';
      break;
    case 'cancelled':
      color = 'error';
      break;
    default:
      color = 'default';
  }
  
  return {
    fontWeight: 500,
    borderRadius: theme.shape.borderRadius,
    textTransform: 'uppercase',
    fontSize: '0.75rem',
    backgroundColor: color === 'default' ? undefined : theme.palette[color].lighter,
    color: color === 'default' ? undefined : theme.palette[color].main,
    borderColor: color === 'default' ? undefined : theme.palette[color].main,
  };
});

const SummaryCard = styled(Card)(({ theme, cardcolor = 'primary' }) => ({
  height: '100%',
  borderRadius: 24,
  background: `linear-gradient(135deg, ${theme.palette[cardcolor].main}15, ${theme.palette[cardcolor].main}05)`,
  border: `1.5px solid black`,
  boxShadow: `0 8px 32px ${theme.palette[cardcolor].main}15`,
  position: 'relative',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: `0 20px 40px ${theme.palette[cardcolor].main}25`,
    '& .summary-icon': {
      transform: 'scale(1.1) rotate(5deg)',
    }
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 4,
    background: `linear-gradient(90deg, ${theme.palette[cardcolor].main}, ${theme.palette[cardcolor].dark})`
  }
}));

const SummaryCardContent = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(4),
  flexGrow: 1,
  position: 'relative',
  zIndex: 1
}));

const SummaryIconWrapper = styled(Box)(({ theme, color = 'primary' }) => ({
  width: 70,
  height: 70,
  borderRadius: 20,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: `linear-gradient(135deg, ${theme.palette[color].main}, ${theme.palette[color].dark})`,
  color: 'white',
  marginBottom: theme.spacing(3),
  boxShadow: `0 8px 24px ${theme.palette[color].main}40`,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
}));

const EmptyState = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(6),
  maxWidth: 500,
  margin: '0 auto'
}));

const InvoicesPage = ({ currentCompany }) => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const { hasPermission } = usePermissions();

  // State
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [companyName, setCompanyName] = useState('');
  
  // UI State
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterDueDate, setFilterDueDate] = useState('all');
  const [sortBy, setSortBy] = useState('number_desc');
  
  // Invoice metrics
  const [metrics, setMetrics] = useState({
    total: 0,
    totalDue: 0,
    overdue: 0,
    paid: 0,
    draft: 0
  });
  
  // Menu state
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  
  // Load data on mount
  useEffect(() => {
    fetchInvoices();
    fetchCompanyData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);
  
  // Apply filters when any filter changes
  useEffect(() => {
    applyFilters();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoices, searchTerm, filterStatus, filterType, filterDueDate, sortBy]);


  
  // Fetch invoices from Supabase
  const fetchInvoices = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // First fetch invoices with customers
      const { data, error } = await supabase
        .from('invoices')
        .select(`
          *,
          customers (id, name, email)
        `)
        .eq('company_id', companyId);
      
      if (error) throw error;
      
      if (data) {
        // Fetch vendors separately for invoices that have vendor_id
        const vendorIds = [...new Set(data.filter(inv => inv.vendor_id).map(inv => inv.vendor_id))];
        let vendorsMap = {};
        
        if (vendorIds.length > 0) {
          try {
            const { data: vendors, error: vendorsError } = await supabase
              .from('vendors')
              .select('id, name, email')
              .in('id', vendorIds);
            
            if (!vendorsError && vendors) {
              vendors.forEach(vendor => {
                vendorsMap[vendor.id] = vendor;
              });
            }
          } catch (vendorErr) {
            console.warn('Could not fetch vendors:', vendorErr);
          }
        }
        
        // Attach vendor data to invoices
        const enrichedData = data.map(invoice => ({
          ...invoice,
          vendors: invoice.vendor_id ? vendorsMap[invoice.vendor_id] : null
        }));
        
        // Check and update overdue invoices
        const updatedData = await checkAndUpdateOverdueInvoices(enrichedData);
        setInvoices(updatedData);
        
        // Calculate metrics with updated data
        calculateMetrics(updatedData);
      }
    } catch (err) {
      console.error('Error fetching invoices:', err);
      setError('Failed to load invoices: ' + err.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Check and update overdue invoices
  const checkAndUpdateOverdueInvoices = async (invoices) => {
    const today = (() => {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    })();

    const invoicesToUpdate = [];
    const updatedInvoices = [...invoices];

    // Find invoices that should be marked as overdue
    invoices.forEach((invoice, index) => {
      if (
        invoice.due_date &&
        invoice.due_date < today &&
        invoice.status !== 'paid' &&
        invoice.status !== 'cancelled' &&
        invoice.status !== 'draft' &&
        invoice.status !== 'overdue'
      ) {
        invoicesToUpdate.push(invoice.id);
        updatedInvoices[index] = { ...invoice, status: 'overdue' };
      }
    });

    // Update overdue invoices in the database
    if (invoicesToUpdate.length > 0) {
      try {
        const { error: updateError } = await supabase
          .from('invoices')
          .update({ status: 'overdue' })
          .in('id', invoicesToUpdate);

        if (updateError) {
          console.error('Error updating overdue invoices:', updateError);
        } 
      } catch (err) {
        console.error('Error in batch update:', err);
      }
    }

    return updatedInvoices;
  };
  
  // Calculate invoice metrics for the summary cards
  const calculateMetrics = (data) => {
    const today = (() => {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    })(); // Format: YYYY-MM-DD
    
    const metrics = {
      total: data.length,
      totalDue: 0,
      overdue: 0,
      paid: 0,
      draft: 0
    };
    
    data.forEach(invoice => {
      // Count by status
      if (invoice.status === 'paid') {
        metrics.paid++;
      } else if (invoice.status === 'draft') {
        metrics.draft++;
      }
      
      // Calculate total due (unpaid invoices)
      if (invoice.status !== 'paid' && invoice.status !== 'cancelled' && invoice.status !== 'draft') {
        metrics.totalDue += invoice.amount_due || invoice.total_amount || 0;
      }
      
      // Count overdue invoices 
      // Method 1: Direct status check for "overdue"
      if (invoice.status && invoice.status.toLowerCase() === 'overdue') {
        metrics.overdue++;
      }
      // Method 2: Date-based overdue check for non-paid/cancelled/draft invoices
      else if (
        invoice.status !== 'paid' && 
        invoice.status !== 'cancelled' && 
        invoice.status !== 'draft' && 
        invoice.due_date && 
        invoice.due_date < today
      ) {
        metrics.overdue++;
      }
    });
    
    setMetrics(metrics);
  };
  
  // Filter and sort invoices
  const applyFilters = () => {
    if (!invoices || invoices.length === 0) {
      setFilteredInvoices([]);
      return;
    }
    
    let filtered = [...invoices];
    const today = (() => {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    })();
    
    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(invoice => 
        (invoice.invoice_number?.toLowerCase().includes(term)) ||
        (invoice.customers?.name?.toLowerCase().includes(term)) ||
        (invoice.vendors?.name?.toLowerCase().includes(term))
      );
    }
    
    // Apply status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(invoice => invoice.status === filterStatus);
    }
    
    // Apply type filter
    if (filterType !== 'all') {
      if (filterType === 'customer') {
        filtered = filtered.filter(invoice => invoice.customer_id !== null);
      } else if (filterType === 'vendor') {
        filtered = filtered.filter(invoice => invoice.vendor_id !== null);
      }
    }
    
    // Apply due date filter
    if (filterDueDate !== 'all') {
      const now = new Date();
      
      if (filterDueDate === 'overdue') {
        filtered = filtered.filter(invoice => {
          // Method 1: Direct status check
          if (invoice.status && invoice.status.toLowerCase() === 'overdue') {
            return true;
          }
          // Method 2: Date-based check
          return (
            invoice.status !== 'paid' && 
            invoice.status !== 'cancelled' && 
            invoice.status !== 'draft' && 
            invoice.due_date && 
            invoice.due_date < today
          );
        });
      } else if (filterDueDate === 'due_today') {
        filtered = filtered.filter(invoice => 
          invoice.status !== 'paid' && 
          invoice.status !== 'cancelled' && 
          invoice.status !== 'draft' && 
          invoice.due_date === today
        );
      } else if (filterDueDate === 'due_this_week') {
        const endOfWeek = new Date(now);
        endOfWeek.setDate(now.getDate() + 7);
        const endOfWeekStr = (() => {
          const year = endOfWeek.getFullYear();
          const month = String(endOfWeek.getMonth() + 1).padStart(2, '0');
          const day = String(endOfWeek.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        })();
        
        filtered = filtered.filter(invoice => 
          invoice.status !== 'paid' && 
          invoice.status !== 'cancelled' && 
          invoice.status !== 'draft' && 
          invoice.due_date && 
          invoice.due_date >= today && 
          invoice.due_date <= endOfWeekStr
        );
      } else if (filterDueDate === 'due_this_month') {
        const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        const endOfMonthStr = (() => {
          const year = endOfMonth.getFullYear();
          const month = String(endOfMonth.getMonth() + 1).padStart(2, '0');
          const day = String(endOfMonth.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        })();
        
        filtered = filtered.filter(invoice => 
          invoice.status !== 'paid' && 
          invoice.status !== 'cancelled' && 
          invoice.status !== 'draft' && 
          invoice.due_date && 
          invoice.due_date >= today && 
          invoice.due_date <= endOfMonthStr
        );
      }
    }
    
    // Apply sort
    if (sortBy === 'date_desc') {
      filtered.sort((a, b) => new Date(b.invoice_date) - new Date(a.invoice_date));
    } else if (sortBy === 'date_asc') {
      filtered.sort((a, b) => new Date(a.invoice_date) - new Date(b.invoice_date));
    } else if (sortBy === 'due_date_desc') {
      filtered.sort((a, b) => {
        if (!a.due_date) return 1;
        if (!b.due_date) return -1;
        return new Date(b.due_date) - new Date(a.due_date);
      });
    } else if (sortBy === 'due_date_asc') {
      filtered.sort((a, b) => {
        if (!a.due_date) return 1;
        if (!b.due_date) return -1;
        return new Date(a.due_date) - new Date(b.due_date);
      });
    } else if (sortBy === 'amount_desc') {
      filtered.sort((a, b) => (b.total_amount || 0) - (a.total_amount || 0));
    } else if (sortBy === 'amount_asc') {
      filtered.sort((a, b) => (a.total_amount || 0) - (b.total_amount || 0));
    } else if (sortBy === 'number_asc') {
      filtered.sort((a, b) => {
        if (!a.invoice_number || !b.invoice_number) return 0;
        // Extract numeric parts for proper numeric sorting
        // Handles formats: INV-YYYYMM-NNNN, INV-YYYY-NNNN, INV-YYYYMM-NNN
        const extractNumeric = (str) => {
          // Match: INV-YYYYMM-NNNN or INV-YYYY-NNNN
          const match = str.match(/INV-(\d{4})(\d{2})?-(\d+)/);
          if (match) {
            const year = parseInt(match[1], 10);
            const month = match[2] ? parseInt(match[2], 10) : 0;
            const num = parseInt(match[3], 10);
            // Create a sortable number: YYYYMMNNNN (year*1000000 + month*10000 + num)
            return year * 1000000 + month * 10000 + num;
          }
          // Fallback: try to extract any numbers
          const fallback = str.match(/\d+/g);
          if (fallback && fallback.length > 0) {
            return parseInt(fallback.join(''), 10) || 0;
          }
          return 0;
        };
        const aNum = extractNumeric(a.invoice_number);
        const bNum = extractNumeric(b.invoice_number);
        if (aNum !== bNum) {
          return aNum - bNum;
        }
        // If numeric values are equal, fall back to string comparison
        return a.invoice_number.localeCompare(b.invoice_number);
      });
    } else if (sortBy === 'number_desc') {
      filtered.sort((a, b) => {
        if (!a.invoice_number || !b.invoice_number) return 0;
        // Extract numeric parts for proper numeric sorting
        // Handles formats: INV-YYYYMM-NNNN, INV-YYYY-NNNN, INV-YYYYMM-NNN
        const extractNumeric = (str) => {
          // Match: INV-YYYYMM-NNNN or INV-YYYY-NNNN
          const match = str.match(/INV-(\d{4})(\d{2})?-(\d+)/);
          if (match) {
            const year = parseInt(match[1], 10);
            const month = match[2] ? parseInt(match[2], 10) : 0;
            const num = parseInt(match[3], 10);
            // Create a sortable number: YYYYMMNNNN (year*1000000 + month*10000 + num)
            return year * 1000000 + month * 10000 + num;
          }
          // Fallback: try to extract any numbers
          const fallback = str.match(/\d+/g);
          if (fallback && fallback.length > 0) {
            return parseInt(fallback.join(''), 10) || 0;
          }
          return 0;
        };
        const aNum = extractNumeric(a.invoice_number);
        const bNum = extractNumeric(b.invoice_number);
        if (aNum !== bNum) {
          return bNum - aNum;
        }
        // If numeric values are equal, fall back to string comparison (reversed for desc)
        return b.invoice_number.localeCompare(a.invoice_number);
      });
    }
    
    setFilteredInvoices(filtered);
    setPage(0); // Reset to first page when filters change
  };
  
  // Menu handlers
  const handleOpenMenu = (event, invoice) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedInvoice(invoice);
  };
  
  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
  };
  
  // Dialog handlers
  const handleOpenDeleteDialog = () => {
    setDeleteDialogOpen(true);
    handleCloseMenu();
  };
  
  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
  };
  
  // Mark invoice as paid
  const handleMarkAsPaid = async () => {
    if (!selectedInvoice) return;
    
    // Check edit permission before proceeding
    if (!hasPermission('sales.invoices.edit', 'write')) {
      setError('You do not have permission to update invoices.');
      handleCloseMenu();
      return;
    }
    
    try {
      // Update invoice status to paid
      const { error } = await supabase
        .from('invoices')
        .update({ status: 'paid' })
        .eq('id', selectedInvoice.id);
      
      if (error) throw error;
      
      // Update local state
      const updatedInvoices = invoices.map(inv => 
        inv.id === selectedInvoice.id 
          ? { ...inv, status: 'paid' }
          : inv
      );
      setInvoices(updatedInvoices);
      calculateMetrics(updatedInvoices);
      
      handleCloseMenu();
    } catch (err) {
      console.error('Error marking invoice as paid:', err);
      setError('Failed to mark invoice as paid: ' + err.message);
      handleCloseMenu();
    }
  };
  
  // Delete invoice
  const handleDeleteInvoice = async () => {
    if (!selectedInvoice) return;
    
    // Check delete permission before proceeding
    if (!hasPermission('sales.invoices.delete', 'delete')) {
      setError('You do not have permission to delete invoices.');
      handleCloseDeleteDialog();
      return;
    }
    
    try {
      // Delete line items first (due to foreign key constraints)
      await supabase
        .from('invoice_items')
        .delete()
        .eq('invoice_id', selectedInvoice.id);
      
      // Then delete the invoice
      const { error } = await supabase
        .from('invoices')
        .delete()
        .eq('id', selectedInvoice.id);
      
      if (error) throw error;
      
      // Update local state
      const updatedInvoices = invoices.filter(inv => inv.id !== selectedInvoice.id);
      setInvoices(updatedInvoices);
      calculateMetrics(updatedInvoices);
      
      handleCloseDeleteDialog();
    } catch (err) {
      console.error('Error deleting invoice:', err);
      setError('Failed to delete invoice: ' + err.message);
    }
  };
  
  // Table pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Utility function to format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    // Parse as local date to avoid timezone shifts
    const [year, month, day] = dateString.split('-');
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString();
  };
  
  // Utility function to format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };
  
  // Utility function to get invoice name (customer or vendor)
  const getInvoiceName = (invoice) => {
    if (invoice.customer_id && invoice.customers) {
      return invoice.customers.name;
    } else if (invoice.vendor_id && invoice.vendors) {
      return invoice.vendors.name;
    }
    return 'Unknown';
  };
  
  // Utility function to get invoice type icon
  const getInvoiceTypeIcon = (invoice) => {
    if (invoice.customer_id) {
      return <PersonIcon fontSize="small" />;
    } else if (invoice.vendor_id) {
      return <BusinessIcon fontSize="small" />;
    }
    return <ReceiptIcon fontSize="small" />;
  };
  
  // Utility function to check if invoice is overdue 
  const isOverdue = (invoice) => {
    // Method 1: Direct status check
    if (invoice.status && invoice.status.toLowerCase() === 'overdue') {
      return true;
    }
    
    // Method 2: Date-based check
    if (invoice.status === 'paid' || invoice.status === 'cancelled' || invoice.status === 'draft' || !invoice.due_date) {
      return false;
    }
    
    const today = (() => {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    })();
    return invoice.due_date < today;
  };
  
  // Reset all filters
  const resetFilters = () => {
    setSearchTerm('');
    setFilterStatus('all');
    setFilterType('all');
    setFilterDueDate('all');
    setSortBy('number_desc');
  };
  
  // Navigate to create new invoice
  const handleCreateInvoice = () => {
    navigate(`/company/${companyId}/invoices/new`);
  };
  
  // Fetch company data
  const fetchCompanyData = async () => {
    try {
      if (currentCompany && currentCompany.name) {
        setCompanyName(currentCompany.name);
        return;
      }

      const { data, error } = await supabase
        .from('companies')
        .select('name')
        .eq('id', companyId)
        .single();

      if (error) throw error;

      if (data) {
        setCompanyName(data.name);
      }
    } catch (err) {
      console.error('Error fetching company data:', err);
      setCompanyName('Company Name');
    }
  };
  

  // Permission check
  if (!hasPermission('sales.invoices.view')) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '70vh',
          textAlign: 'center'
        }}>
          <Card sx={{ 
            p: 8, 
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
            maxWidth: 600,
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            cursor: 'default',
            transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
              opacity: 0.8,
              transition: 'opacity 0.3s ease'
            },
            '&:hover::before': {
              opacity: 1
            }
          }}>
            <Box sx={{ 
              p: 3, 
              borderRadius: 3, 
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              mb: 6,
              display: 'inline-flex',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
            }}>
              <ReceiptIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            </Box>
            <Typography variant="h3" sx={{ 
              mb: 3, 
              fontWeight: 500, 
              color: 'text.primary',
              fontSize: '2.5rem',
              letterSpacing: '-0.8px'
            }}>
              Access Restricted
            </Typography>
            <Typography variant="h6" sx={{ 
              color: 'text.secondary', 
              mb: 4,
              fontWeight: 500,
              fontSize: '1.2rem'
            }}>
              Invoice Directory Permissions Required
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'text.secondary',
              lineHeight: 1.7,
              mb: 6,
              fontSize: '1.05rem',
              maxWidth: 450,
              mx: 'auto'
            }}>
              You don't currently have the necessary permissions to access the Invoice Directory. 
              Contact your system administrator to request access or verify your role permissions.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => window.history.back()}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                px: 4,
                py: 1.5,
                fontSize: '1rem'
              }}
            >
              Return to Previous Page
            </Button>
          </Card>
        </Box>
      </Container>
    );
  }
  
  return (
    <Fade in timeout={800}>
      <Box>
        <Container 
          maxWidth="xl"
          sx={{
            py: 3,
            pb: 8,
            px: { xs: 2, sm: 3 },
            width: '100%',
            maxWidth: '100vw !important'
          }}
        >
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Header Section */}
      <Box sx={{ mb: 5, pt: 3 }}>
        {/* Enhanced Page Header */}
        <Card sx={{
          borderRadius: 3,
          mb: 3,
          mt: 5,
          border: `1px solid ${theme.palette.grey[400]}`,
          background: theme.palette.grey[100],
          backdropFilter: 'blur(20px)',
          boxShadow: 'none',
          borderTop: '3px solid #2196F3',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 6px 20px ${alpha('#2196F3', 0.15)}`,
          },
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 2 }}>
              <Box sx={{
                p: 2,
                borderRadius: 3,
                background: alpha('#2196F3', 0.08),
                border: `1px solid ${alpha('#2196F3', 0.15)}`,
              }}>
                <ReceiptIcon sx={{ fontSize: 32, color: '#2196F3' }} />
              </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h4" sx={{
                fontWeight: 500,
                color: '#2196F3',
                mb: 1,
                fontSize: '2rem',
                letterSpacing: '-0.5px'
              }}>
                Invoice Management
              </Typography>
              <Typography variant="body1" sx={{ 
                color: 'text.secondary', 
                fontWeight: 500,
                fontSize: '1rem'
              }}>
                {companyName || 'Loading...'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button 
                startIcon={<RefreshIcon />}
                onClick={fetchInvoices}
                variant="outlined"
                sx={{ 
                  borderRadius: 2,
                  borderColor: 'grey.400',
                  color: 'primary.main',
                  borderWidth: 1,
                  fontWeight: 500,
                  px: 3,
                  py: 1.5,
                  background: 'white',
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  '&:hover': {
                    borderColor: 'grey.600',
                    backgroundColor: 'white',
                    transform: 'translateY(-1px)',
                    boxShadow: `0 4px 12px ${alpha(theme.palette.grey[400], 0.25)}`
                  }
                }}
              >
                Refresh
              </Button>
              {hasPermission('sales.invoices.edit', 'write') && (
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleCreateInvoice}
                  sx={{
                    borderRadius: 2,
                    borderColor: 'grey.400',
                    color: 'primary.main',
                    borderWidth: 1,
                    fontWeight: 500,
                    px: 3,
                    py: 1.5,
                    background: 'white',
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  '&:hover': {
                    borderColor: 'grey.600',
                    backgroundColor: 'white',
                    transform: 'translateY(-1px)',
                    boxShadow: `0 4px 12px ${alpha(theme.palette.grey[400], 0.25)}`
                  }
                }}
              >
                New Invoice
              </Button>
              )}
            </Box>
          </Box>
            <Typography variant="body1" sx={{ 
              color: 'text.secondary', 
              maxWidth: 700, 
              lineHeight: 1.6,
              fontSize: '1.05rem',
              fontWeight: 500
            }}>
              Manage your customer and vendor invoices with comprehensive tracking, filtering tools, 
              and real-time status monitoring across your entire business ecosystem.
            </Typography>
          </CardContent>
        </Card>
      </Box>
      
      {/* Summary Cards */}
      <Box sx={{ 
        mb: 5, 
        px: 1,
        animation: 'slideInUp 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '@keyframes slideInUp': {
          '0%': {
            transform: 'translateY(100%)',
            opacity: 0,
          },
          '100%': {
            transform: 'translateY(0)',
            opacity: 1,
          }
        }
      }}>
        <Grid container spacing={6} sx={{ maxWidth: '100%', width: '100%' }}>
          <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 0 }}>
            <Card 
              sx={{ 
                height: '100%',
                background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.08)}, ${alpha(theme.palette.info.main, 0.02)})`,
                border: `1px solid ${theme.palette.info.main}`,
                borderRadius: 3,
                boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
                transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                position: 'relative',
                overflow: 'hidden',
                cursor: 'default',
                '&:hover': { 
                  transform: 'translateY(-4px)',
                  boxShadow: `0 20px 40px ${alpha(theme.palette.info.main, 0.25)}`,
                  bgcolor: 'white'
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.03)}, transparent)`,
                  zIndex: 0,
                  transition: 'opacity 0.3s ease',
                },
                '&:hover::before': {
                  opacity: 0.8
                }
              }}
            >
              <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="h3" sx={{ 
                      fontWeight: 500, 
                      color: 'info.main', 
                      mb: 1,
                      fontSize: '2rem',
                      lineHeight: 1
                    }}>
                      {metrics.total}
                    </Typography>
                    <Typography variant="body1" color="text.primary" sx={{ 
                      fontWeight: 500,
                      fontSize: '1rem'
                    }}>
                      Total Invoices
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mt: 0.5 }}>
                      {metrics.draft} draft{metrics.draft !== 1 && 's'}
                    </Typography>
                  </Box>
                  <Box 
                    sx={{ 
                      p: 2.5, 
                      borderRadius: 3, 
                      background: theme.palette.info.main,
                      color: 'white',
                      boxShadow: `0 6px 18px ${alpha(theme.palette.info.main, 0.3)}`,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
                    }}
                  >
                    <ReceiptIcon sx={{ fontSize: 32 }} />
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 0 }}>
            <Card 
              sx={{ 
                height: '100%',
                background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.08)}, ${alpha(theme.palette.warning.main, 0.02)})`,
                border: `1px solid ${theme.palette.warning.main}`,
                borderRadius: 3,
                boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
                transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                position: 'relative',
                overflow: 'hidden',
                cursor: 'default',
                '&:hover': { 
                  transform: 'translateY(-4px)',
                  boxShadow: `0 20px 40px ${alpha(theme.palette.warning.main, 0.25)}`,
                  bgcolor: 'white'
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.03)}, transparent)`,
                  zIndex: 0,
                  transition: 'opacity 0.3s ease',
                },
                '&:hover::before': {
                  opacity: 0.8
                }
              }}
            >
              <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="h3" sx={{ 
                      fontWeight: 500, 
                      color: 'warning.main', 
                      mb: 1,
                      fontSize: '2rem',
                      lineHeight: 1
                    }}>
                      {formatCurrency(metrics.totalDue)}
                    </Typography>
                    <Typography variant="body1" color="text.primary" sx={{ 
                      fontWeight: 500,
                      fontSize: '1rem'
                    }}>
                      Amount Due
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mt: 0.5 }}>
                      {metrics.total - metrics.paid - metrics.draft} unpaid invoice{(metrics.total - metrics.paid - metrics.draft) !== 1 && 's'}
                    </Typography>
                  </Box>
                  <Box 
                    sx={{ 
                      p: 2.5, 
                      borderRadius: 3, 
                      background: theme.palette.warning.main,
                      color: 'white',
                      boxShadow: `0 6px 18px ${alpha(theme.palette.warning.main, 0.3)}`,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
                    }}
                  >
                    <MoneyIcon sx={{ fontSize: 32 }} />
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 0 }}>
            <Card 
              sx={{ 
                height: '100%',
                background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.08)}, ${alpha(theme.palette.error.main, 0.02)})`,
                border: `1px solid ${theme.palette.error.main}`,
                borderRadius: 3,
                boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
                transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                position: 'relative',
                overflow: 'hidden',
                cursor: 'default',
                '&:hover': { 
                  transform: 'translateY(-4px)',
                  boxShadow: `0 20px 40px ${alpha(theme.palette.error.main, 0.25)}`,
                  bgcolor: 'white'
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.03)}, transparent)`,
                  zIndex: 0,
                  transition: 'opacity 0.3s ease',
                },
                '&:hover::before': {
                  opacity: 0.8
                }
              }}
            >
              <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="h3" sx={{ 
                      fontWeight: 500, 
                      color: 'error.main', 
                      mb: 1,
                      fontSize: '2rem',
                      lineHeight: 1
                    }}>
                      {metrics.overdue}
                    </Typography>
                    <Typography variant="body1" color="text.primary" sx={{ 
                      fontWeight: 500,
                      fontSize: '1rem'
                    }}>
                      Overdue
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mt: 0.5 }}>
                      Requiring attention
                    </Typography>
                  </Box>
                  <Box 
                    sx={{ 
                      p: 2.5, 
                      borderRadius: 3, 
                      background: theme.palette.error.main,
                      color: 'white',
                      boxShadow: `0 6px 18px ${alpha(theme.palette.error.main, 0.3)}`,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
                    }}
                  >
                    <AccessTimeIcon sx={{ fontSize: 32 }} />
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 0 }}>
            <Card 
              sx={{ 
                height: '100%',
                background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.08)}, ${alpha(theme.palette.success.main, 0.02)})`,
                border: `1px solid ${theme.palette.success.main}`,
                borderRadius: 3,
                boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
                transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                position: 'relative',
                overflow: 'hidden',
                cursor: 'default',
                '&:hover': { 
                  transform: 'translateY(-4px)',
                  boxShadow: `0 20px 40px ${alpha(theme.palette.success.main, 0.25)}`,
                  bgcolor: 'white'
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.03)}, transparent)`,
                  zIndex: 0,
                  transition: 'opacity 0.3s ease',
                },
                '&:hover::before': {
                  opacity: 0.8
                }
              }}
            >
              <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="h3" sx={{ 
                      fontWeight: 500, 
                      color: 'success.main', 
                      mb: 1,
                      fontSize: '2rem',
                      lineHeight: 1
                    }}>
                      {metrics.paid}
                    </Typography>
                    <Typography variant="body1" color="text.primary" sx={{ 
                      fontWeight: 500,
                      fontSize: '1rem'
                    }}>
                      Paid Invoices
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mt: 0.5 }}>
                      {((metrics.paid / (metrics.total || 1)) * 100).toFixed(0)}% completion rate
                    </Typography>
                  </Box>
                  <Box 
                    sx={{ 
                      p: 2.5, 
                      borderRadius: 3, 
                      background: theme.palette.success.main,
                      color: 'white',
                      boxShadow: `0 6px 18px ${alpha(theme.palette.success.main, 0.3)}`,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
                    }}
                  >
                    <CheckCircleIcon sx={{ fontSize: 32 }} />
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
      
      {/* Filters and Search */}
      <Paper 
        sx={{ 
          p: 4, 
          mb: 4, 
          borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
          boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          animation: 'slideInUp 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '@keyframes slideInUp': {
            '0%': {
              transform: 'translateY(100%)',
              opacity: 0,
            },
            '100%': {
              transform: 'translateY(0)',
              opacity: 1,
            }
          },
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
          }
        }}
      >
        <Typography 
          variant="h6" 
          sx={{ 
            mb: 3, 
            fontWeight: 500,
            color: 'primary.main',
            fontSize: '1.25rem'
          }}
        >
          Filter & Search Invoices
        </Typography>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search by invoice number or name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: theme.palette.primary.main }} />
                  </InputAdornment>
                ),
                sx: { 
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: 'white',
                  '&:hover': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                  },
                  '&.Mui-focused': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.25)}`
                  }
                }
              }}
              size="medium"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    border: 'none'
                  },
                  '&:hover fieldset': {
                    border: 'none'
                  },
                  '&.Mui-focused fieldset': {
                    border: 'none'
                  }
                }
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="medium">
              <InputLabel 
                id="status-filter-label" 
                sx={{ 
                  fontSize: '0.9rem', 
                  fontWeight: 500,
                  transform: 'translate(14px, 12px) scale(1)',
                  '&.Mui-focused, &.MuiFormLabel-filled': {
                    fontSize: '0.9rem',
                    transform: 'translate(14px, -20px) scale(0.75)'
                  }
                }}
              >
                Status
              </InputLabel>
              <Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                label="Status"
                sx={{
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: 'white',
                  minHeight: 52,
                  '& .MuiSelect-select': {
                    py: 1.5,
                    px: 2,
                    fontSize: '0.9rem',
                    fontWeight: 500,
                    minHeight: 'auto',
                    display: 'flex',
                    alignItems: 'center'
                  },
                  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                  '&:hover': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                  },
                  '&.Mui-focused': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                  }
                }}
              >
                <MenuItem value="all" sx={{ fontSize: '0.9rem', fontWeight: 500, py: 1.5, px: 2 }}>All Statuses</MenuItem>
                <MenuItem value="pending" sx={{ fontSize: '0.9rem', fontWeight: 500, py: 1.5, px: 2 }}>Pending</MenuItem>
                <MenuItem value="sent" sx={{ fontSize: '0.9rem', fontWeight: 500, py: 1.5, px: 2 }}>Sent</MenuItem>
                <MenuItem value="paid" sx={{ fontSize: '0.9rem', fontWeight: 500, py: 1.5, px: 2 }}>Paid</MenuItem>
                <MenuItem value="overdue" sx={{ fontSize: '0.9rem', fontWeight: 500, py: 1.5, px: 2 }}>Overdue</MenuItem>
                <MenuItem value="draft" sx={{ fontSize: '0.9rem', fontWeight: 500, py: 1.5, px: 2 }}>Draft</MenuItem>
                <MenuItem value="cancelled" sx={{ fontSize: '0.9rem', fontWeight: 500, py: 1.5, px: 2 }}>Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="medium">
              <InputLabel 
                id="type-filter-label" 
                sx={{ 
                  fontSize: '0.9rem', 
                  fontWeight: 500,
                  transform: 'translate(14px, 12px) scale(1)',
                  '&.Mui-focused, &.MuiFormLabel-filled': {
                    fontSize: '0.9rem',
                    transform: 'translate(14px, -20px) scale(0.75)'
                  }
                }}
              >
                Type
              </InputLabel>
              <Select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                label="Type"
                sx={{
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: 'white',
                  minHeight: 52,
                  '& .MuiSelect-select': {
                    py: 1.5,
                    px: 2,
                    fontSize: '0.9rem',
                    fontWeight: 500,
                    minHeight: 'auto',
                    display: 'flex',
                    alignItems: 'center'
                  },
                  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                  '&:hover': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                  },
                  '&.Mui-focused': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                  }
                }}
              >
                <MenuItem value="all" sx={{ fontSize: '0.9rem', fontWeight: 500, py: 1.5, px: 2 }}>All Types</MenuItem>
                <MenuItem value="customer" sx={{ fontSize: '0.9rem', fontWeight: 500, py: 1.5, px: 2 }}>Customer</MenuItem>
                <MenuItem value="vendor" sx={{ fontSize: '0.9rem', fontWeight: 500, py: 1.5, px: 2 }}>Vendor</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="medium">
              <InputLabel 
                id="due-date-filter-label" 
                sx={{ 
                  fontSize: '0.9rem', 
                  fontWeight: 500,
                  transform: 'translate(14px, 12px) scale(1)',
                  '&.Mui-focused, &.MuiFormLabel-filled': {
                    fontSize: '0.9rem',
                    transform: 'translate(14px, -20px) scale(0.75)'
                  }
                }}
              >
                Due Date
              </InputLabel>
              <Select
                value={filterDueDate}
                onChange={(e) => setFilterDueDate(e.target.value)}
                label="Due Date"
                sx={{
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: 'white',
                  minHeight: 52,
                  '& .MuiSelect-select': {
                    py: 1.5,
                    px: 2,
                    fontSize: '0.9rem',
                    fontWeight: 500,
                    minHeight: 'auto',
                    display: 'flex',
                    alignItems: 'center'
                  },
                  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                  '&:hover': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                  },
                  '&.Mui-focused': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                  }
                }}
              >
                <MenuItem value="all" sx={{ fontSize: '0.9rem', fontWeight: 500, py: 1.5, px: 2 }}>All Due Dates</MenuItem>
                <MenuItem value="overdue" sx={{ fontSize: '0.9rem', fontWeight: 500, py: 1.5, px: 2 }}>Overdue</MenuItem>
                <MenuItem value="due_today" sx={{ fontSize: '0.9rem', fontWeight: 500, py: 1.5, px: 2 }}>Due Today</MenuItem>
                <MenuItem value="due_this_week" sx={{ fontSize: '0.9rem', fontWeight: 500, py: 1.5, px: 2 }}>Due This Week</MenuItem>
                <MenuItem value="due_this_month" sx={{ fontSize: '0.9rem', fontWeight: 500, py: 1.5, px: 2 }}>Due This Month</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="medium">
              <InputLabel 
                id="sort-by-label" 
                sx={{ 
                  fontSize: '0.9rem', 
                  fontWeight: 500,
                  transform: 'translate(14px, 12px) scale(1)',
                  '&.Mui-focused, &.MuiFormLabel-filled': {
                    fontSize: '0.9rem',
                    transform: 'translate(14px, -20px) scale(0.75)'
                  }
                }}
              >
                Sort By
              </InputLabel>
              <Select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                label="Sort By"
                sx={{
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: 'white',
                  minHeight: 52,
                  '& .MuiSelect-select': {
                    py: 1.5,
                    px: 2,
                    fontSize: '0.9rem',
                    fontWeight: 500,
                    minHeight: 'auto',
                    display: 'flex',
                    alignItems: 'center'
                  },
                  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                  '&:hover': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                  },
                  '&.Mui-focused': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                  }
                }}
              >
                <MenuItem value="date_desc" sx={{ fontSize: '0.9rem', fontWeight: 500, py: 1.5, px: 2 }}>Date (Newest First)</MenuItem>
                <MenuItem value="date_asc" sx={{ fontSize: '0.9rem', fontWeight: 500, py: 1.5, px: 2 }}>Date (Oldest First)</MenuItem>
                <MenuItem value="due_date_desc" sx={{ fontSize: '0.9rem', fontWeight: 500, py: 1.5, px: 2 }}>Due Date (Newest First)</MenuItem>
                <MenuItem value="due_date_asc" sx={{ fontSize: '0.9rem', fontWeight: 500, py: 1.5, px: 2 }}>Due Date (Oldest First)</MenuItem>
                <MenuItem value="amount_desc" sx={{ fontSize: '0.9rem', fontWeight: 500, py: 1.5, px: 2 }}>Amount (Highest First)</MenuItem>
                <MenuItem value="amount_asc" sx={{ fontSize: '0.9rem', fontWeight: 500, py: 1.5, px: 2 }}>Amount (Lowest First)</MenuItem>
                <MenuItem value="number_asc" sx={{ fontSize: '0.9rem', fontWeight: 500, py: 1.5, px: 2 }}>Invoice # (A-Z)</MenuItem>
                <MenuItem value="number_desc" sx={{ fontSize: '0.9rem', fontWeight: 500, py: 1.5, px: 2 }}>Invoice # (Z-A)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={12}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                {filteredInvoices.length} invoice{filteredInvoices.length !== 1 && 's'} found
              </Typography>
              
              <Button 
                size="small" 
                onClick={resetFilters}
                disabled={searchTerm === '' && filterStatus === 'all' && filterType === 'all' && filterDueDate === 'all' && sortBy === 'number_desc'}
                sx={{ 
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  fontWeight: 500,
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                  }
                }}
              >
                Reset Filters
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Table */}
      {filteredInvoices.length === 0 ? (
        <Paper 
          sx={{
            textAlign: 'center',
            py: 8,
            px: 4,
            border: '2px dashed',
            borderColor: 'divider',
            borderRadius: 2,
          }}
        >
          <Box sx={{ 
            p: 2.5, 
            borderRadius: 3, 
            background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            backdropFilter: 'blur(10px)',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`,
            mb: 3,
            width: 'fit-content',
            mx: 'auto'
          }}>
            <ReceiptIcon sx={{ fontSize: 36, color: 'text.secondary' }} />
          </Box>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
            {invoices.length === 0 ? 'No Invoices Yet' : 'No Matching Invoices'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto', fontWeight: 500 }}>
            {invoices.length === 0 
              ? 'Add your first invoice to start tracking your transactions and payments.'
              : 'Try adjusting your search or filter criteria.'}
          </Typography>
          {invoices.length === 0 && hasPermission('sales.invoices.edit', 'write') && (
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => navigate(`/company/${companyId}/invoices/new`)}
              sx={{ 
                borderRadius: 2,
                borderColor: 'grey.400',
                color: 'primary.main',
                borderWidth: 1,
                fontWeight: 500,
                px: 3,
                py: 1.5,
                background: 'white',
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                '&:hover': {
                  borderColor: 'grey.600',
                  backgroundColor: 'white',
                  transform: 'translateY(-1px)',
                  boxShadow: `0 4px 12px ${alpha(theme.palette.grey[400], 0.25)}`
                }
              }}
            >
              Create Your First Invoice
            </Button>
          )}
        </Paper>
      ) : (
        <Paper sx={{ 
          borderRadius: 3, 
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
          boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
          overflow: 'hidden',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          animation: 'slideInUp 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '@keyframes slideInUp': {
            '0%': {
              transform: 'translateY(100%)',
              opacity: 0,
            },
            '100%': {
              transform: 'translateY(0)',
              opacity: 1,
            }
          },
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
          }
        }}>
          <TableContainer sx={{ overflow: 'visible' }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell 
                    sx={{ 
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      bgcolor: 'white',
                      borderBottom: `2px solid ${theme.palette.primary.main}`,
                      color: 'primary.main',
                      py: 2,
                      px: 3
                    }}
                  >
                    Invoice Number
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      bgcolor: 'white',
                      borderBottom: `2px solid ${theme.palette.primary.main}`,
                      color: 'primary.main',
                      py: 2,
                      px: 3
                    }}
                  >
                    Customer/Vendor
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      bgcolor: 'white',
                      borderBottom: `2px solid ${theme.palette.primary.main}`,
                      color: 'primary.main',
                      py: 2,
                      px: 3
                    }}
                  >
                    Date
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      bgcolor: 'white',
                      borderBottom: `2px solid ${theme.palette.primary.main}`,
                      color: 'primary.main',
                      py: 2,
                      px: 3
                    }}
                  >
                    Due Date
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      bgcolor: 'white',
                      borderBottom: `2px solid ${theme.palette.primary.main}`,
                      color: 'primary.main',
                      py: 2,
                      px: 3
                    }}
                  >
                    Amount
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      bgcolor: 'white',
                      borderBottom: `2px solid ${theme.palette.primary.main}`,
                      color: 'primary.main',
                      py: 2,
                      px: 3
                    }}
                  >
                    Status
                  </TableCell>
                  <TableCell 
                    align="center"
                    sx={{ 
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      bgcolor: 'white',
                      borderBottom: `2px solid ${theme.palette.primary.main}`,
                      color: 'primary.main',
                      py: 2,
                      px: 3,
                      width: 160
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(rowsPerPage > 0
                  ? filteredInvoices.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  : filteredInvoices
                ).map((invoice, index) => (
                  <TableRow 
                    key={invoice.id} 
                    hover
                    onClick={() => navigate(`/company/${companyId}/invoices/edit/${invoice.id}`)}
                    sx={{ 
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': { 
                        bgcolor: 'grey.50',
                        transform: 'translateX(4px)',
                        transition: 'all 0.2s ease',
                        boxShadow: 'inset 4px 0 0 black'
                      },
                      '& .MuiTableCell-root': {
                        borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                        py: 2.5,
                        px: 3
                      }
                    }}
                  >
                    {/* Invoice Number */}
                    <TableCell>
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          fontWeight: 500,
                          color: 'text.primary',
                          fontSize: '0.95rem'
                        }}
                      >
                        {invoice.invoice_number}
                      </Typography>
                    </TableCell>
                    
                    {/* Customer/Vendor */}
                    <TableCell>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 500,
                          color: 'text.primary',
                          fontSize: '0.9rem'
                        }}
                      >
                        {getInvoiceName(invoice)}
                      </Typography>
                    </TableCell>
                    
                    {/* Date */}
                    <TableCell>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 500,
                          color: 'text.primary',
                          fontSize: '0.9rem'
                        }}
                      >
                        {formatDate(invoice.invoice_date)}
                      </Typography>
                    </TableCell>
                    
                    {/* Due Date */}
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {invoice.due_date && isOverdue(invoice) ? (
                          <Tooltip title="Overdue">
                            <ErrorIcon 
                              fontSize="small" 
                              sx={{ 
                                mr: 0.5, 
                                color: theme.palette.error.main,
                                animation: 'pulse 2s infinite'
                              }} 
                            />
                          </Tooltip>
                        ) : null}
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: 500,
                            fontSize: '0.9rem',
                            color: isOverdue(invoice) ? theme.palette.error.main : theme.palette.text.primary
                          }}
                        >
                          {formatDate(invoice.due_date)}
                        </Typography>
                      </Box>
                    </TableCell>
                    
                    {/* Amount */}
                    <TableCell>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 500,
                          color: 'text.primary',
                          fontSize: '0.9rem'
                        }}
                      >
                        {formatCurrency(invoice.amount_due || invoice.total_amount)}
                      </Typography>
                    </TableCell>
                    
                    {/* Status */}
                    <TableCell>
                      <StatusChip 
                        label={invoice.status} 
                        status={invoice.status}
                        size="small"
                        sx={{
                          fontWeight: 500,
                          textTransform: 'uppercase',
                          fontSize: '0.75rem',
                          borderRadius: 2
                        }}
                      />
                    </TableCell>
                    
                    {/* Actions */}
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', alignItems: 'center' }}>
                        <Tooltip title="More options">
                          <IconButton 
                            size="small" 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenMenu(e, invoice);
                            }}
                            disabled={!hasPermission('sales.invoices.edit', 'write') && !hasPermission('sales.invoices.delete', 'delete')}
                            sx={{
                              color: (!hasPermission('sales.invoices.edit', 'write') && !hasPermission('sales.invoices.delete', 'delete')) ? 'text.disabled' : 'text.primary',
                              border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                              borderRadius: 2,
                              transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                              '&:hover': {
                                bgcolor: (!hasPermission('sales.invoices.edit', 'write') && !hasPermission('sales.invoices.delete', 'delete')) ? 'transparent' : 'primary.main',
                                color: (!hasPermission('sales.invoices.edit', 'write') && !hasPermission('sales.invoices.delete', 'delete')) ? 'text.disabled' : 'white',
                                transform: (!hasPermission('sales.invoices.edit', 'write') && !hasPermission('sales.invoices.delete', 'delete')) ? 'none' : 'scale(1.05)',
                                boxShadow: (!hasPermission('sales.invoices.edit', 'write') && !hasPermission('sales.invoices.delete', 'delete')) ? 'none' : `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`
                              },
                              '&.Mui-disabled': {
                                color: 'text.disabled',
                                border: `1px solid ${alpha(theme.palette.grey[300], 0.3)}`
                              }
                            }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          <Box sx={{ 
            borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
            borderRadius: '0 0 12px 12px'
          }}>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredInvoices.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{ 
                px: 3,
                py: 2,
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  color: theme.palette.text.primary
                },
                '& .MuiTablePagination-select': {
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  borderRadius: 2,
                  px: 2,
                  py: 1,
                  fontWeight: 500,
                  fontSize: '0.9rem',
                  background: 'white',
                  '&:hover': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                  }
                },
                '& .MuiTablePagination-actions': {
                  '& .MuiIconButton-root': {
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    borderRadius: 2,
                    margin: '0 2px',
                    background: 'white',
                    color: theme.palette.primary.main,
                    fontWeight: 500,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      background: theme.palette.primary.main,
                      color: 'white',
                      transform: 'translateY(-1px)',
                      border: `1px solid ${theme.palette.primary.main}`,
                      boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                    },
                    '&.Mui-disabled': {
                      background: alpha(theme.palette.grey[300], 0.5),
                      color: theme.palette.grey[500],
                      border: `1px solid ${theme.palette.grey[300]}`,
                      transform: 'none',
                      boxShadow: 'none'
                    }
                  }
                }
              }}
            />
          </Box>
        </Paper>
      )}
      
      {/* Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleCloseMenu}
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            boxShadow: `0 8px 24px ${alpha(theme.palette.grey[900], 0.15)}`,
            minWidth: 180
          }
        }}
      >
        <MenuItem 
          onClick={hasPermission('sales.invoices.edit', 'write') ? () => {
            navigate(`/company/${companyId}/invoices/edit/${selectedInvoice?.id}`);
            handleCloseMenu();
          } : undefined}
          disabled={!hasPermission('sales.invoices.edit', 'write')}
          sx={{ 
            py: 1.5, 
            px: 2,
            borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
            '&:hover': {
              bgcolor: hasPermission('sales.invoices.edit', 'write') ? alpha(theme.palette.primary.main, 0.08) : 'transparent'
            }
          }}
        >
          <ListItemIcon>
            <ReceiptIcon fontSize="small" color={hasPermission('sales.invoices.edit', 'write') ? "primary" : "disabled"} />
          </ListItemIcon>
          <ListItemText sx={{ 
            '& .MuiTypography-root': { 
              fontWeight: 500,
              color: hasPermission('sales.invoices.edit', 'write') ? 'text.primary' : 'text.disabled'
            } 
          }}>
            View/Edit
          </ListItemText>
        </MenuItem>
        
        {/* Mark as Paid - only show if invoice is not already paid */}
        {selectedInvoice?.status !== 'paid' && (
          <MenuItem 
            onClick={hasPermission('sales.invoices.edit', 'write') ? handleMarkAsPaid : undefined}
            disabled={!hasPermission('sales.invoices.edit', 'write') || selectedInvoice?.status === 'paid'}
            sx={{ 
              py: 1.5, 
              px: 2,
              borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
              '&:hover': {
                bgcolor: hasPermission('sales.invoices.edit', 'write') && selectedInvoice?.status !== 'paid' 
                  ? alpha(theme.palette.success.main, 0.08) 
                  : 'transparent'
              }
            }}
          >
            <ListItemIcon>
              <CheckCircleIcon 
                fontSize="small" 
                color={
                  hasPermission('sales.invoices.edit', 'write') && selectedInvoice?.status !== 'paid' 
                    ? "success" 
                    : "disabled"
                } 
              />
            </ListItemIcon>
            <ListItemText sx={{ 
              '& .MuiTypography-root': { 
                fontWeight: 500,
                color: hasPermission('sales.invoices.edit', 'write') && selectedInvoice?.status !== 'paid' 
                  ? 'success.main' 
                  : 'text.disabled'
              } 
            }}>
              Mark as Paid
            </ListItemText>
          </MenuItem>
        )}
        
        <Divider sx={{ borderColor: alpha(theme.palette.grey[400], 0.8) }} />
        
        <MenuItem 
          onClick={hasPermission('sales.invoices.delete', 'delete') ? handleOpenDeleteDialog : undefined}
          disabled={!hasPermission('sales.invoices.delete', 'delete')}
          sx={{ 
            py: 1.5, 
            px: 2,
            '&:hover': {
              bgcolor: hasPermission('sales.invoices.delete', 'delete') ? alpha(theme.palette.error.main, 0.08) : 'transparent'
            }
          }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color={hasPermission('sales.invoices.delete', 'delete') ? "error" : "disabled"} />
          </ListItemIcon>
          <ListItemText sx={{ 
            color: hasPermission('sales.invoices.delete', 'delete') ? 'error.main' : 'text.disabled',
            '& .MuiTypography-root': { fontWeight: 500 } 
          }}>
            Delete
          </ListItemText>
        </MenuItem>
      </Menu>
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        PaperProps={{
          sx: { 
            borderRadius: 3,
          }
        }}
      >
        <DialogTitle sx={{ 
          background: theme.palette.error.main,
          color: 'white',
          p: 3,
          position: 'relative'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <DeleteIcon sx={{ fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: 500 }}>
              Delete Invoice
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3, pt: 4 }}>
          <DialogContentText sx={{ fontSize: '1rem', fontWeight: 500, mt: 2 }}>
            Are you sure you want to delete invoice <strong>#{selectedInvoice?.invoice_number}</strong>? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button 
            onClick={handleCloseDeleteDialog}
            sx={{ 
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              fontWeight: 500,
              px: 3,
              py: 1,
              '&:hover': {
                border: `1px solid ${theme.palette.primary.main}`,
                transform: 'translateY(-1px)',
                boxShadow: `0 4px 12px ${alpha(theme.palette.grey[900], 0.15)}`
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteInvoice} 
            color="error"
            variant="contained"
            sx={{ 
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              fontWeight: 500,
              px: 3,
              py: 1,
              '&:hover': {
                border: `1px solid ${theme.palette.error.main}`,
                transform: 'translateY(-1px)',
                boxShadow: `0 4px 12px ${alpha(theme.palette.error.main, 0.25)}`
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
        </Container>
      </Box>
    </Fade>
  );
};

export default InvoicesPage;